import React from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import Header from './Header';
import SecondaryNavBar from './SecondaryNavBar';
import JobSecondaryNavBar from './JobSecondaryNavBar';
import Footer from './Footer';
import MessagesSidebar from '../MessagesSidebar/MessagesSidebar';
import ConversationsSidebar from '../ConversationsSidebar/ConversationsSidebar';
import MinimizedJobApplicationWidget from '../Documents/MinimizedJobApplicationWidget';
import AIApplicationWriterModal from '../Documents/AIApplicationWriterModal';
import { useUIStore } from '../../store/uiStore';
import { useAppMode } from '../../contexts/AppModeContext';
import { useWebsiteSettings } from '../../contexts/WebsiteSettingsContext';

interface LayoutProps {
  children: React.ReactNode;
}

const Layout: React.FC<LayoutProps> = ({ children }) => {
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isTouch = useMediaQuery('(hover: none) and (pointer: coarse)');
  const isAdminRoute = location.pathname === '/admin' || location.pathname.startsWith('/admin/');
  const isAdminDashboardRoute = location.pathname === '/admin';
  const isMessagesRoute = location.pathname.startsWith('/messages');
  const { settings } = useWebsiteSettings();
  const { 
    messagesSidebarOpen, 
    setMessagesSidebarOpen,
    conversationsSidebarOpen,
    setConversationsSidebarOpen,
    conversationsSidebarProduct,
    jobApplicationWriterMinimized,
    jobApplicationWriterOpen,
    setJobApplicationWriterMinimized,
    setJobApplicationWriterOpen,
    selectedJobsCount
  } = useUIStore();
  const chromeHidden = !!useUIStore((s) => (s as any).chromeHidden);
  const { mode } = useAppMode();
  const isJobMode = mode === 'job';
  const hasShopSecondary = !chromeHidden && !isAdminDashboardRoute && !isMessagesRoute && !isJobMode && !isMobile && !isTouch;
  const hasJobSecondary = !chromeHidden && !isAdminDashboardRoute && !isMessagesRoute && isJobMode && !isMobile && !isTouch;
  const hasAnySecondary = hasShopSecondary || hasJobSecondary;
  const footerCfg: any = (settings as any)?.footer;
  const footerEnabled = footerCfg?.enabled !== false;
  const footerShowOnHomepage = footerCfg?.showOnHomepage !== false;
  const isHomepage = location.pathname === '/';
  const shouldRenderFooter = !isAdminRoute && !isMessagesRoute && footerEnabled && (!isHomepage || footerShowOnHomepage);

  return (
    <Box sx={{ 
      display: 'flex', 
      flexDirection: 'column', 
      minHeight: '100vh',
      ...(isMessagesRoute && {
        height: '100vh',
        overflow: 'hidden',
      })
    }}>
      {!isAdminDashboardRoute && !chromeHidden && <Header />}
      {/* Secondary navigation bars: shop or job modes on desktop/tablet non-touch */}
      {hasShopSecondary && <SecondaryNavBar />}
      {hasJobSecondary && <JobSecondaryNavBar />}
      <Box 
        component="main" 
        sx={{ 
          flexGrow: 1, 
          // Push content below fixed bars. Header heights: xs=44px, md=52px. Add +40px if secondary is present.
          pt: chromeHidden || isAdminDashboardRoute || isMessagesRoute
            ? 0
            : {
                xs: `${44 + (hasAnySecondary ? 40 : 0)}px`,
                md: `${52 + (hasAnySecondary ? 40 : 0)}px`,
              },
          ...(isMessagesRoute && {
            // Account for fixed header height responsively
            height: { xs: 'calc(100vh - 44px)', md: 'calc(100vh - 52px)' },
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
            margin: 0,
            padding: 0,
            position: 'relative',
            marginTop: { xs: '44px', md: '52px' },
            boxSizing: 'border-box',
          })
        }}
      >
        {children}
      </Box>
      {shouldRenderFooter && <Footer />}
      
      {/* Global Messages Sidebar */}
      <MessagesSidebar
        open={messagesSidebarOpen}
        onClose={() => setMessagesSidebarOpen(false)}
      />
      
      {/* Global Conversations Sidebar */}
      <ConversationsSidebar
        open={conversationsSidebarOpen}
        onClose={() => setConversationsSidebarOpen(false)}
        productId={conversationsSidebarProduct?.productId}
        productName={conversationsSidebarProduct?.productName}
        sellerId={conversationsSidebarProduct?.sellerId}
        sellerName={conversationsSidebarProduct?.sellerName}
      />
      
      {/* Global Minimized Job Application Widget */}
      {mode === 'job' && (
        <MinimizedJobApplicationWidget
          isVisible={jobApplicationWriterMinimized && selectedJobsCount > 0}
          onRestore={() => {
            setJobApplicationWriterMinimized(false);
            setJobApplicationWriterOpen(true);
          }}
          hasUnfinishedApplication={jobApplicationWriterOpen}
        />
      )}
      
      {/* Global AI Application Writer Modal */}
      {mode === 'job' && (
        <AIApplicationWriterModal
          open={jobApplicationWriterOpen && !jobApplicationWriterMinimized}
          onClose={() => setJobApplicationWriterOpen(false)}
          document={null}
          onMinimize={() => setJobApplicationWriterMinimized(true)}
          onRestore={() => setJobApplicationWriterMinimized(false)}
        />
      )}
    </Box>
  );
};

export default Layout;
